import { FC, PropsWithChildren, useMemo, useState } from 'react';
import { capitalize } from '@mui/material';

import { AtsValidationErrors, IetfError, ValidationError } from '@models/ValidationError';
import {
  getValidationErrorMap,
  isAtsValidationError,
  isIetfValidationError,
} from '@utils/validationErrorUtils';

import ValidationContext, { ValidationValues } from './context';

type ValidationProviderProps = PropsWithChildren<{
  fieldNames?: string[];
}>;

const ValidationProvider: FC<ValidationProviderProps> = ({ fieldNames = [], children }) => {
  const [atsValidationErrors, setAtsValidationErrors] = useState<AtsValidationErrors>();
  const [ietfValidationError, setIetfValidationError] = useState<IetfError>();

  const validationErrorMap = useMemo(
    () => getValidationErrorMap(fieldNames, atsValidationErrors?.Message, ietfValidationError),
    [atsValidationErrors, ietfValidationError, fieldNames]
  );

  const getFieldError = (fieldName: string) => {
    if (atsValidationErrors?.Message && Array.isArray(atsValidationErrors.Message))
      return atsValidationErrors.Message.find(({ Field }) => Field === fieldName)?.Message;

    return (ietfValidationError?.errors?.[fieldName] ||
      ietfValidationError?.errors?.[capitalize(fieldName)])?.[0];
  };

  const getGeneralErrors = () => {
    if (!fieldNames) return;
    if (atsValidationErrors?.Message && Array.isArray(atsValidationErrors.Message))
      return atsValidationErrors.Message.filter(({ Field }) => fieldNames.includes(Field));
  };

  const setValidationErrors = (validationError: ValidationError) => {
    if (isAtsValidationError(validationError)) setAtsValidationErrors(validationError);
    else if (isIetfValidationError(validationError)) setIetfValidationError(validationError);
  };

  const clearValidationErrors = () => {
    setAtsValidationErrors(undefined);
    setIetfValidationError(undefined);
  };

  const values: ValidationValues = {
    clearValidationErrors,
    getFieldError,
    getGeneralErrors,
    setValidationErrors,
    validationErrorMap,
  };

  return <ValidationContext.Provider value={values}>{children}</ValidationContext.Provider>;
};

export default ValidationProvider;
